import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/dist/query/react';
import { CreateAndModifySurvey, Survey, SurveyResult } from '../../types/surveyData';
import cookies from 'js-cookie';

export const surveyApi = createApi({
  reducerPath: 'surveyApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_SURVEY_API,
    credentials: 'include',
    prepareHeaders: (headers, { getState }) => {
      const xsrfToken = cookies.get('XSRF-TOKEN');
      if (xsrfToken) {
        headers.set('X-XSRF-TOKEN', xsrfToken);
      }
      return headers;
    },
  }),
  tagTypes: ['Surveys'],
  endpoints: (builder) => ({
    getSurveyList: builder.query<Survey[], any>({
      query: (url: any) => url,
      transformResponse(response: any): Promise<any> {
        return response.data;
      },
      providesTags: ['Surveys'],
    }),
    getBuiltSurveyList: builder.query<Survey[], any>({
      query: (url: any) => url,
      transformResponse(response: any): Promise<any> {
        return response.data;
      },
    }),
    getSurveyDetail: builder.query<Survey, string>({
      query: (url: string) => `${url}`,
      transformResponse(response: any): Promise<any> {
        return response.data;
      },
    }),
    getSurveyDetailOnlySurvey: builder.query<any, string>({
      query: (surveyId: string) => ({ url: `${surveyId}/detailOnlySurvey`, method: 'GET' }),
      transformResponse(response: any): Promise<any> {
        return response.data;
      },
    }),
    getSurveyDetailMutation: builder.mutation<Survey, any>({
      query: (url: string) => `${url}`,
      transformResponse(response: any): Promise<any> {
        return response.data;
      },
    }),
    deleteSurvey: builder.mutation<{ success: boolean }, any>({
      query: (surveyId: string) => ({ url: surveyId, method: 'DELETE' }),
      invalidatesTags: ['Surveys'],
    }),
    copySurveyMutation: builder.mutation<any, string>({
      query: (surveyId: string) => ({ url: `${surveyId}/copy`, method: 'GET' }),
      transformResponse(response: any): Promise<any> {
        return response.data;
      },
    }),
    createAndModifySurvey: builder.mutation<any, CreateAndModifySurvey | Survey>({
      query: (body: CreateAndModifySurvey) => ({ url: ``, method: 'POST', body }),
      transformResponse(response: any): Promise<any> {
        return response.data;
      },
    }),
    createAndModifyResponse: builder.mutation<any, any>({
      query: (param: any) => {
        const { url, body } = param;
        return {
          url: url,
          method: 'POST',
          body,
        };
      },
      /* transformResponse(response: any): Promise<any> {
        return response.data;
      },*/
    }),
    createAndModifyQuestion: builder.mutation<any, any>({
      query: (param: any) => ({ url: `/${param.surveyId}/question`, method: 'POST', body: param.body }),
      transformResponse(response: any): Promise<any> {
        return response.data;
      },
    }),
    copyQuestion: builder.mutation<any, any>({
      query: (param: any) => ({ url: `/${param.surveyId}/question/${param.questionId}/copy`, method: 'GET' }),
      transformResponse(response: any): Promise<any> {
        return response.data;
      },
    }),
    deleteQuestion: builder.mutation<{ success: boolean }, any>({
      query: (url: string) => ({ url: url, method: 'DELETE' }),
    }),
    createAndModifyQueOption: builder.mutation<any, any>({
      query: (param: any) => ({
        url: `/${param.surveyId}/question/${param.questionId}/option`,
        method: 'POST',
        body: param.body,
      }),
      transformResponse(response: any): Promise<any> {
        return response.data;
      },
    }),
    deleteQueOption: builder.mutation<{ success: boolean }, any>({
      query: (url: string) => ({ url: url, method: 'DELETE' }),
      transformResponse(response: any): Promise<any> {
        return response.data;
      },
    }),
    getResultSummary: builder.query<SurveyResult, string>({
      query: (url: string) => `${url}`,
      transformResponse(response: any): Promise<any> {
        return response.data;
      },
    }),
    getSurveyDetailAll: builder.query<any, string>({
      query: (surveyId: string) => `/${surveyId}/detail`,
      transformResponse(response: any): Promise<any> {
        return response.data;
      },
    }),
    deleteResponse: builder.mutation<{ success: boolean }, any>({
      query: (url: string) => ({ url: url, method: 'DELETE' }),
      transformResponse(response: any): Promise<any> {
        return response;
      },
    }),
    getResultIndividual: builder.query<any, string>({
      query: (surveyId: string) => `/${surveyId}/result/participant`,
      transformResponse(response: any): Promise<any> {
        return response.data;
      },
    }),
    getResultIndividualDetail: builder.query<any, string>({
      query: (url: string) => `${url}`,
      transformResponse(response: any): Promise<any> {
        return response.data;
      },
    }),
    createAndModifyShareParam: builder.mutation<any, any>({
      query: (param: any) => ({
        url: `/${param.surveyId}/distribute`,
        method: 'POST',
        body: param.body,
        headers: { 'Content-Type': 'application/json' },
      }),
      transformResponse(response: any): Promise<any> {
        return response.data;
      },
    }),
    updateViewer: builder.mutation<any, any>({
      query: (param: any) => ({
        url: `/${param.surveyId}/viewer`,
        method: 'POST',
        body: param.body,
        headers: { 'Content-Type': 'application/json' },
      }),
      transformResponse(response: any): Promise<any> {
        return response.data;
      },
    }),
    getResponseUrl: builder.query<any, string>({
      query: (url: string) => `${process.env.REACT_APP_SURVEY_API}/generated?shortUrl=${url}`,
      transformResponse(response: any): Promise<any> {
        return response.data;
      },
    }),
    uploadSurveyImage: builder.mutation<any, any>({
      query: (param: any) => {
        return {
          url: `image/upload/survey`,
          method: 'POST',
          body: param,
        };
      },
      transformResponse(response: any): Promise<any> {
        return response.data;
      },
    }),
    deleteSurveyImage: builder.mutation<{ success: boolean }, any>({
      query: (param: { surveyId: string; fileGroupId: string }) => ({
        url: `image/delete/survey?surveyId=${param.surveyId}&fileGroupId=${param.fileGroupId}`,
        method: 'DELETE',
      }),
    }),
    uploadOptionImage: builder.mutation<any, any>({
      query: (param: any) => {
        return {
          url: `image/upload/option`,
          method: 'POST',
          body: param,
        };
      },
      transformResponse(response: any): Promise<any> {
        return response.data;
      },
    }),
    deleteOptionImage: builder.mutation<{ success: boolean }, any>({
      query: (param: { optionId: string; fileGroupId: string }) => ({
        url: `image/delete/option?optionId=${param.optionId}&fileGroupId=${param.fileGroupId}`,
        method: 'DELETE',
      }),
    }),
    getDownloadFile: builder.query<any, string>({
      query: (fileId: string) => `${process.env.REACT_APP_SURVEY_API}/image/download?fileId=${fileId}`,
      transformResponse(response: any): Promise<any> {
        return response.data;
      },
    }),
    resetSurveyDate: builder.mutation<any, any>({
      query: (param: any) => ({ url: `/${param.surveyId}/reset`, method: 'POST', body: param.body }),
      transformResponse(response: any): Promise<any> {
        return response.data;
      },
    }),
    botAlertTarget: builder.mutation<any, string>({
      query: (surveyId: string) => ({ url: `/bot/target?surveyId=${surveyId}`, method: 'GET' }),
    }),
    botAlertResponse: builder.mutation<any, string>({
      query: (surveyId: string) => ({ url: `/bot/response?surveyId=${surveyId}`, method: 'GET' }),
    }),
    botAlertReserve: builder.mutation<any, string>({
      query: (surveyId: string) => ({ url: `/bot/reserve?surveyId=${surveyId}`, method: 'GET' }),
    }),
  }),
});

export const {
  useGetSurveyListQuery,
  useGetSurveyDetailQuery,
  useGetSurveyDetailOnlySurveyQuery,
  useGetSurveyDetailMutationMutation,
  useDeleteSurveyMutation,
  useGetBuiltSurveyListQuery,
  useCopySurveyMutationMutation,
  useCreateAndModifySurveyMutation,
  useCreateAndModifyResponseMutation,
  useCreateAndModifyQuestionMutation,
  useUpdateViewerMutation,
  useDeleteQuestionMutation,
  useCreateAndModifyQueOptionMutation,
  useCopyQuestionMutation,
  useDeleteQueOptionMutation,
  useGetResultSummaryQuery,
  useGetSurveyDetailAllQuery,
  useDeleteResponseMutation,
  useGetResultIndividualQuery,
  useGetResultIndividualDetailQuery,
  useCreateAndModifyShareParamMutation,
  useGetResponseUrlQuery,
  useUploadSurveyImageMutation,
  useDeleteSurveyImageMutation,
  useUploadOptionImageMutation,
  useDeleteOptionImageMutation,
  useGetDownloadFileQuery,
  useResetSurveyDateMutation,
  useBotAlertTargetMutation,
  useBotAlertResponseMutation,
  useBotAlertReserveMutation,
} = surveyApi;
